"use strict";
/* eslint-disable no-param-reassign */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.setHostProduct = exports.getHostProduct = exports.HOST_PRODUCT_MESSAGE_RESPONSE_TIMEOUT_MS = exports.RESPONSE_WITH_HOST_PRODUCT_INFO_MESSAGE = exports.GET_HOST_PRODUCT_INFO_MESSAGE = void 0;
/*
SetHostProduct

Summary:
"Host" / "Component" products are a common setup within AutoDesk. A
host product is the main product that is being run (like AutoCAD Web). It might
import other components from other teams though (like the Large Model Viewer to
view PDFs). These imports are often called "components" or "add-ins". Both the
host and the components might be separately using the Web Analytics SDK to track
user events. However, this tracking data is most useful when those events can be
linked together and our analysts can say "These events came from the same
machine and same session - some events are from the host and some are from the
components". That's when the SetHostProduct API is used. When the host calls
setHostProduct(), we now want all component events in the same browser to
contain info about the host. Additionally, we now want events coming from the
host to contain info about the various components in the browser.

More information for the Desktop SDK version of this API can be found at:
https://wiki.autodesk.com/pages/viewpage.action?spaceKey=ADP&title=Application+Interface#ApplicationInterface-SetHostProductAPI

Technical overview:
This isn't too complicated when the host and component share the same browser
window. When the host calls setHostProduct(), we store the host info on a global
"_adpHostProduct" field on the window. Additionally, we store a callback on the
window ("newComponentCallback") that the host provides. That way when the
component retrieves this information from the window using getHostProduct(), it
can also inform the host of it's own information by calling the callback.

It gets more complicated when the component is imported into the product using
an iFrame. In this scenario, we can't pass information using the window because
the main application and the component in the iFrame don't share the same
window. Thus we use message passing. When a component calls getHostProduct() and
the information isn't on the window, then it will post a message to it's
window's parent asking for the productHost information and listen for a
response. It also passes along information about itself so that the host knows
about the component. When the host calls setHostProduct(), it adds an
eventListener to the window which listens for this productHost information
request. When it receives the request, it doesn't know where it came from so we
send a response back to all iFrames on the page with the productHost
information. If the component doesn't get a productHost information response
back within a set timeout (see global variable below), then it will timeout and
assume that there is no product host (or that the product host hasn't called the
setHostProduct API yet).
*/
/* Constants */
exports.GET_HOST_PRODUCT_INFO_MESSAGE = "adp-sdk-get-product-host";
exports.RESPONSE_WITH_HOST_PRODUCT_INFO_MESSAGE = "adp-sdk-here-is-product-host";
exports.HOST_PRODUCT_MESSAGE_RESPONSE_TIMEOUT_MS = 500;
/* Functions */
function setHostProductOnWindow(hostProduct, win, newComponentCallback) {
    win._adpHostProduct = {
        info: hostProduct,
        newComponentCallback,
    };
}
function getHostProduct(componentInfo, win = window) {
    var _a, _b;
    return __awaiter(this, void 0, void 0, function* () {
        // First let's see if we have the info on our window
        if (win._adpHostProduct != null) {
            (_b = (_a = win._adpHostProduct).newComponentCallback) === null || _b === void 0 ? void 0 : _b.call(_a, componentInfo);
            return win._adpHostProduct.info;
        }
        // Ok, we must be an iFrame. Let's use message passing to get the host info
        return Promise.race([
            // Let's race to see if we receive a message back with HostProduct
            // information before we reach a specified timeout. We do this so that we
            // don't hang if the parent doesn't have product host info.
            new Promise((resolve) => {
                setTimeout(() => resolve(null), exports.HOST_PRODUCT_MESSAGE_RESPONSE_TIMEOUT_MS);
            }),
            new Promise((resolve) => {
                var _a, _b;
                // Setup the listener before sending our request for the product host so
                // that we don't create a race condition
                win.addEventListener("message", (message) => {
                    var _a;
                    if (((_a = message === null || message === void 0 ? void 0 : message.data) === null || _a === void 0 ? void 0 : _a.messageType) === exports.RESPONSE_WITH_HOST_PRODUCT_INFO_MESSAGE) {
                        // We received a product host message back! Let's cache our result on
                        // the window for faster lookup next time and resolve this promise
                        const { hostProduct } = message.data;
                        setHostProductOnWindow(hostProduct, win);
                        resolve(hostProduct);
                    }
                });
                const message = {
                    messageType: exports.GET_HOST_PRODUCT_INFO_MESSAGE,
                    componentInfo,
                };
                (_b = (_a = win.parent) === null || _a === void 0 ? void 0 : _a.postMessage) === null || _b === void 0 ? void 0 : _b.call(_a, message, "*");
            }),
        ]);
    });
}
exports.getHostProduct = getHostProduct;
function setHostProduct({ product_id, session_id }, newComponentCallback, win = window) {
    const hostProduct = {
        product_id,
        session_id,
        lastUpdated: new Date(),
    };
    setHostProductOnWindow(hostProduct, win, newComponentCallback);
    // Now that we're the Host if any iFrames ask in the future for HostProduct
    // info, let's respond with our info and save their component info using the
    // provided callback
    win.addEventListener("message", (message) => {
        var _a, _b, _c;
        if (((_a = message === null || message === void 0 ? void 0 : message.data) === null || _a === void 0 ? void 0 : _a.messageType) === exports.GET_HOST_PRODUCT_INFO_MESSAGE) {
            const messageWithResponse = {
                messageType: exports.RESPONSE_WITH_HOST_PRODUCT_INFO_MESSAGE,
                hostProduct,
            };
            // eslint-disable-next-line no-restricted-syntax
            for (const iFrame of document.getElementsByTagName("iframe")) {
                (_c = (_b = iFrame.contentWindow) === null || _b === void 0 ? void 0 : _b.postMessage) === null || _c === void 0 ? void 0 : _c.call(_b, messageWithResponse, "*");
            }
            const { componentInfo } = message.data;
            newComponentCallback(componentInfo);
        }
    });
}
exports.setHostProduct = setHostProduct;
